import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import cx from 'classnames';
import { useTable, useSortBy, useExpanded, useGlobalFilter } from 'react-table';
import { Table } from 'react-bootstrap';
import { useForecast } from '../../providers/ForecastProvider';
import { useProjection } from '../../providers/ProjectionProvider';
import useForecastCalculator from '../../hooks/useForecastCalculator';
import { calculateRevenueGrowth, calculateSalesGrowth } from '../../utils/calculation';
import { hideLoading, showLoading } from '../../lib/uiService';
import TableHead from '../../components/common/TableHead';
import TableBody from '../../components/common/TableBody';
import { useProductProjectionsTableColumns } from './ProductProjectionsTableHooks';
import { toast } from 'react-toastify';
import { UserRole } from '../../lib/types';
import { useAuth } from '../../providers/AuthProvider';

const ProductProjectionsTable: React.FC<any> = ({ projection }: { projection: any }) => {
  const { id: projectionId } = useParams();
  const { role } = useAuth();
  const { forecastId, territoryId, hasForecasts, forecastCalendarId, isForecastClosed } = useForecast();
  const [productForecasts, setProductForecasts] = useState<any[]>([]);
  const [initialData, setInitialData] = useState<any[]>([]);
  const projectionContext = useProjection();
  const baseQuantityRef = React.useRef<any>({});
  const [isDirty, setIsDirty] = useState(false);
  const [isAutoSave, setIsAutoSave] = useState(false);
  const { forecastCalculator, loadForecastCalculator } = useForecastCalculator();
  const ref = React.useRef<any>({});
  Object.assign(ref.current, { forecastCalculator });

  useEffect(() => {
    const localStorageCalendarId = localStorage.getItem('TimePeriod') || '';
    const calendarId =
      forecastCalendarId && forecastCalendarId !== 'undefined' ? forecastCalendarId : localStorageCalendarId;
    const currentTerritoryId =
      territoryId && territoryId !== 'undefined' ? territoryId : localStorage.getItem('Territory');
    if (hasForecasts && forecastId && calendarId && currentTerritoryId) {
      if (projection) {
        const changes = projection.getProductChanges();
        // filter changes by territoryId and calendarId
        const filteredChanges = changes.filter((change: any) => {
          return (
            change.forecastProduct.territoryId === currentTerritoryId &&
            change.forecastProduct.forecastCalendarId === calendarId
          );
        });
        filteredChanges.forEach((change: any) => {
          const { forecastProductId, quantity, salesGrowth } = change.forecastProduct;
          baseQuantityRef.current[forecastProductId] = (Number(quantity) * 100) / (Number(salesGrowth) + 100);
        });

        loadForecastCalculator(forecastId, calendarId, currentTerritoryId);

        setInitialData(filteredChanges);
        setProductForecasts(filteredChanges);
      } else {
        setInitialData([]);
        setProductForecasts([]);
      }
    }
  }, [forecastId, territoryId, forecastCalendarId, projection]);

  useEffect(() => {
    if (isAutoSave) handleSave();
  }, [productForecasts]);

  const handleChange = (id: string, updatedData: any) => {
    setProductForecasts((prevData) => {
      const prevItem = prevData.find((item: any) => item.forecastProductProjectionId === id);
      const { forecastCalculator } = ref.current;
      const baseQuantity = baseQuantityRef.current[prevItem.forecastProductId];
      if (updatedData.quantity !== undefined && baseQuantity && !prevItem.isZeroCase) {
        const salesGrowth = calculateSalesGrowth(updatedData.quantity, baseQuantity);
        updatedData.salesGrowth = salesGrowth;
      }
      if (Number(updatedData.salesGrowth) === -100) {
        return prevData;
      }

      forecastCalculator.forecastProductCollection.updateForecastProductGrowth({
        ...prevItem.forecastProduct,
        ...prevItem,
        ...updatedData,
      });
      const updatedItems = forecastCalculator.getForecastProducts();

      return prevData.map((row) => {
        if (row.forecastProductProjectionId === id) {
          const updatedItem = updatedItems.find((item: any) => item.forecastProductId === prevItem.forecastProductId);
          const newRow = {
            ...row,
            ...updatedData,
          };
          const newForecastRevenue = updatedItem.revenue;
          const newRevenueGrowth = calculateRevenueGrowth(newRow['salesGrowth'], newRow['priceGrowth']);
          const newForecastQuantity = updatedItem.quantity;
          return {
            ...newRow,
            quantity: newForecastQuantity,
            revenue: newForecastRevenue,
            revenueGrowth: newRevenueGrowth,
          };
        } else {
          return row;
        }
      });
    });
    setIsDirty(true);
  };

  const handleRemove = (id: string) => {
    setProductForecasts((prevData) => {
      return prevData.filter((row) => row.forecastProductProjectionId !== id);
    });
    setIsAutoSave(true);
  };

  const handleSave = () => {
    showLoading();
    const changedRows = productForecasts.filter((productForecast, index) => {
      return (
        productForecast.salesGrowth !== initialData[index].salesGrowth ||
        productForecast.priceGrowth !== initialData[index].priceGrowth
      );
    });
    if (changedRows.length) {
      const data = changedRows.map(({ forecastProductId, salesGrowth, priceGrowth }) => ({
        forecastProductId,
        salesGrowth,
        priceGrowth,
      }));

      projectionContext.handlers
        .update({
          territoryId,
          projectionId,
          isExec: false,
          data,
        })
        .then(() => {
          hideLoading();
          setInitialData(productForecasts);
          toast.success('Successfully Updated Product Projection');
        })
        .catch((error) => {
          hideLoading();
          console.log(error);
        });
    }
    // find removed rows
    const removedRows = initialData.filter((initialRow) => {
      return !productForecasts.find(
        (row) => row.forecastProductProjectionId === initialRow.forecastProductProjectionId,
      );
    });
    if (removedRows.length) {
      const data = removedRows.map(({ forecastProductProjectionId }) => ({
        forecastProductProjectionId,
      }));
      projectionContext.handlers
        .discardLine({
          territoryId,
          projectionId,
          data,
        })
        .then(() => {
          projectionContext.handlers.fetch(forecastId, {
            calendarId: forecastCalendarId,
            territoryId,
            checkUser: !role?.includes(UserRole.Admin),
          });
          hideLoading();
          setInitialData(productForecasts);
          toast.success('Successfully discarded Product Projection');
        })
        .catch((error) => {
          hideLoading();
          console.log(error);
        });
    }
    setIsDirty(false);
    setIsAutoSave(false);
  };

  const handleDiscardAll = () => {
    productForecasts.forEach((row) => {
      handleRemove(row.forecastProductProjectionId);
    });
    setIsAutoSave(true);
  };
  const handleSaveItem = React.useCallback(
    (changedData?: any) => {
      const changedRows = changedData;

      if (changedRows.length === 0) return;
      const data = changedRows.map(({ forecastProductId, salesGrowth, priceGrowth, explanation, quantity }: any) => ({
        forecastProductId,
        salesGrowth,
        priceGrowth,
        explanation,
        quantity,
      }));
      showLoading();
      projectionContext.handlers
        .update({
          territoryId,
          forecastId,
          isExec: false,
          data,
        })
        .then(() => {
          hideLoading();
          setInitialData(productForecasts);
          toast.success('Successfully Updated Product Projection');
        })
        .catch((error) => {
          hideLoading();
          console.log(error);
        });
      hideLoading();
    },
    [productForecasts],
  );

  const isEditable = projection?.projection?.isCommitted !== true;

  const columns = useProductProjectionsTableColumns({
    handleChange,
    handleRemove,
    handleSaveItem,
    isForecastClosed,
    isEditable,
  });

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: useMemo(() => productForecasts, [productForecasts]),
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
  );

  return (
    <div className="card shadow" style={{ marginBottom: '44px' }}>
      <div className="card-header py-3">
        <p className="text-primary m-0 fw-bold">Product Projections</p>
      </div>
      <div className="card-body">
        <div>
          <Table striped className="table-sm" bordered {...getTableProps()} responsive>
            <TableHead headerGroups={headerGroups} />
            <TableBody
              {...getTableBodyProps()}
              rows={rows}
              renderRow={(row: any, key: number) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={key} className={`table-body-font-size`}>
                    {row.cells.map((cell: any, index: number) => {
                      return (
                        <td
                          {...cell.getCellProps()}
                          key={`${key}-${index}`}
                          style={{
                            border: 'none',
                            backgroundColor: '#fff',
                          }}
                          className={'text-dark align-middle'}
                        >
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              }}
            />
            <tfoot>
              <tr>
                {columns.map((column: any, index: number) => {
                  return (
                    <td key={index} className="cursor-pointer">
                      {column?.Footer?.({ data: productForecasts })}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </Table>
        </div>
        {isEditable && (
          <div className="row">
            <div className="col-6">
              <button
                disabled={productForecasts.length === 0 || isForecastClosed}
                className="btn btn-secondary float-start"
                type="button"
                onClick={handleDiscardAll}
              >
                Discard All Product Projections
              </button>
            </div>
            <div className="col-6">
              <button
                className={cx('btn float-end', isDirty ? 'btn-primary' : 'btn-secondary')}
                type="button"
                onClick={handleSave}
                disabled={!isDirty}
              >
                Update
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductProjectionsTable;
